import {
  useGetSubscription,
  useListBillingPlans,
  useListPaymentMethods,
  useUpdateSubscription,
} from "@/api/stripe";
import { ROUTES } from "@/constants/routes";
import { PlanConfigurationModal } from "@/pages/billing/PlanConfigurationModal";
import {
  buildPlanChangeAction,
  transformBillingPlans,
  type PlanSelection,
  type TransformedPlan,
} from "@/utils/planChange";
import { route } from "@/utils/route";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

/**
 * Buying seats — the picker, and what happens when it resolves.
 *
 * Two surfaces offer this: the capacity prompt on Members (which only appears
 * once you're full) and the Usage rail's standing "Add seats" action. One hook
 * so they land in the same place: an in-place subscription update when the
 * plan allows it, otherwise Stripe checkout.
 *
 * `redirectTo` is where checkout returns you, so each caller comes back to the
 * surface it started from.
 */
export function useSeatPurchase(redirectTo: string = ROUTES.SETTINGS.MEMBERS) {
  const navigate = useNavigate();
  const { data: subscription } = useGetSubscription();
  const { data: billingPlans } = useListBillingPlans();
  const { data: paymentMethods } = useListPaymentMethods();
  const updateSubscription = useUpdateSubscription();

  const [selectedPlan, setSelectedPlan] = useState<TransformedPlan | null>(
    null,
  );
  const [isOpen, setIsOpen] = useState(false);

  const plans = useMemo(
    () => transformBillingPlans(billingPlans?.data.billing_plans),
    [billingPlans],
  );

  const openSeatPicker = () => {
    const currentPlanName = subscription?.data.stripe_subscription?.plan_name;
    const planToShow =
      plans.find((plan) => plan.name === currentPlanName) || plans[0];
    if (!planToShow) return;
    setSelectedPlan(planToShow);
    setIsOpen(true);
  };

  const handleComplete = async (selection: PlanSelection) => {
    setIsOpen(false);

    const planChange = buildPlanChangeAction({
      selection,
      subscription: subscription?.data.stripe_subscription,
      paymentMethods: paymentMethods?.data.payment_methods,
    });

    if (planChange.type === "update") {
      try {
        await updateSubscription.mutateAsync(planChange.payload);
        toast.success("Plan updated successfully");
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to update plan",
        );
      }
      return;
    }

    navigate(
      route(
        ROUTES.BILLING.CHECKOUT,
        {},
        {
          plan: selection.plan.name,
          billing_cycle: selection.billing_cycle,
          user_count: String(selection.user_count),
          redirect_to: redirectTo,
        },
      ),
    );
  };

  /** Mount this wherever the trigger lives; it renders nothing until opened. */
  const seatPicker = selectedPlan ? (
    <PlanConfigurationModal
      plan={selectedPlan}
      open={isOpen}
      buttonLoading={updateSubscription.isPending}
      redirectTo={redirectTo}
      onComplete={handleComplete}
      onClose={() => setIsOpen(false)}
    />
  ) : null;

  return { openSeatPicker, seatPicker };
}
